import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { formatKES, formatPaymentMethod } from '@/lib/utils'
import type { PaymentMethodBreakdownRow } from '@riverpark/types'

const METHOD_COLORS: Record<string, string> = {
  mpesa: 'hsl(152 56% 27%)',
  c2b_mpesa: 'hsl(152 40% 45%)',
  manual_mpesa: 'hsl(84 40% 45%)',
  cash: 'hsl(38 88% 42%)',
  bank_transfer: 'hsl(221 70% 53%)',
  credit_note: 'hsl(262 55% 55%)',
}
const FALLBACK_COLOR = 'hsl(210 17% 55%)'

function MethodTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload: PaymentMethodBreakdownRow }[]
}) {
  if (!active || !payload?.length) return null
  const row = payload[0]!.payload
  return (
    <div className="rounded border border-border-2 bg-card px-3 py-2 shadow-modal">
      <p className="font-sans text-xs font-medium text-ink">{formatPaymentMethod(row.method)}</p>
      <p className="font-sans text-sm font-medium text-status-paid-text">
        {formatKES(row.totalCollected)}
      </p>
      <p className="font-sans text-[11px] text-muted-foreground">
        {row.count} payment{row.count === 1 ? '' : 's'}
      </p>
    </div>
  )
}

export function PaymentMethodChart({
  data,
  isLoading,
}: {
  data: PaymentMethodBreakdownRow[]
  isLoading: boolean
}) {
  if (isLoading) return <p className="font-sans text-sm text-muted-foreground">Loading…</p>
  if (data.length === 0) {
    return (
      <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
        No payments in this range.
      </p>
    )
  }

  return (
    <div className="h-64 rounded border border-border-2 bg-card p-4">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="totalCollected"
            nameKey="method"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={2}
          >
            {data.map((row) => (
              <Cell key={row.method} fill={METHOD_COLORS[row.method] ?? FALLBACK_COLOR} />
            ))}
          </Pie>
          <Tooltip content={<MethodTooltip />} />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={(value: string) => (
              <span className="font-sans text-xs text-ink">{formatPaymentMethod(value)}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
